/**
 * Why one file was refused, and what the person holding it should do.
 *
 * This exists because of a real capture. Ten photographs were selected, nine
 * were stored, and the tenth was refused with:
 *
 *   "Gönderilen bilgiler kabul edilmedi. Alanları kontrol edin."
 *
 * There were no fields on that screen. The file was a PNG that had been
 * renamed to `.jpg`, and the only thing that would have helped was being told
 * so. `ERROR_MESSAGES` is keyed by the *kind* of failure, and four different
 * upload refusals share `validation` there - an empty file, a mislabelled one,
 * a corrupt one and one whose resolution is beyond what the server decodes.
 * Their remedies have nothing in common.
 *
 * So the server now sends a `code` beside the status, from the closed
 * vocabulary in `sentyform.ingestion.errors`, and this file turns it into two
 * sentences: **what happened**, and **what to do next**. Both are chosen here,
 * in Turkish. The server's `detail` is still never rendered - it can name a
 * path, a codec or a library.
 *
 * An unrecognised code falls back by status, and an unrecognised status falls
 * back to a sentence that admits the failure is unexplained rather than
 * inventing a cause. Guessing here would be worse than the generic message it
 * replaced: a user told to re-shoot a photograph that was actually rejected by
 * a full disk has been sent to do the wrong work.
 */

import type { ApiError } from "@/api/errors";

export interface UploadFailure {
  /** What happened, in one sentence, with no internal vocabulary in it. */
  what: string;
  /** What the user can do about it. Never "contact support" as a first move. */
  next: string;
}

const BY_CODE: Record<string, UploadFailure> = {
  upload_content_mismatch: {
    what: "Dosyanın içeriği, adındaki uzantıyla uyuşmuyor.",
    next: "Dosyayı gerçek biçiminde yeniden kaydedin veya doğru uzantıyla adlandırıp tekrar yükleyin.",
  },
  upload_unsupported_type: {
    what: "Bu dosya biçimi desteklenmiyor.",
    next: "Desteklenen biçimler listesindeki bir türe dönüştürüp tekrar yükleyin.",
  },
  upload_unreadable: {
    what: "Dosya okunamadı; içeriği bozuk veya eksik görünüyor.",
    next: "Dosyayı kaynağından yeniden kopyalayın veya aynı kareyi yeniden çekin.",
  },
  upload_resolution_too_high: {
    what: "Fotoğrafın çözünürlüğü, bu sunucunun işleyebileceğinden büyük.",
    next: "Fotoğrafı daha düşük çözünürlükte kaydedip tekrar yükleyin.",
  },
  upload_too_large: {
    what: "Dosya, sunucunun kabul ettiği boyuttan büyük.",
    next: "Daha küçük bir dosya yükleyin; video ise kısaltmayı veya daha düşük kalitede dışa aktarmayı deneyin.",
  },
  upload_empty: {
    what: "Dosya boş.",
    next: "Dosyanın tamamen kopyalandığından emin olup tekrar deneyin.",
  },
  upload_unsupported_codec: {
    what: "Video dosyası tanındı, ancak içindeki video biçimi bu sunucuda açılamıyor.",
    next: "Videoyu MP4 (H.264) olarak dönüştürüp tekrar yükleyin.",
  },
  upload_storage_failed: {
    what: "Sunucu dosyayı kaydedemedi.",
    next: "Birkaç dakika sonra tekrar deneyin; sorun sürerse diğer dosyalar etkilenmedi.",
  },
  upload_failed: {
    what: "Sunucu bu dosyayı kabul etmedi.",
    next: "Dosyayı kontrol edip tekrar deneyin; diğer dosyalar etkilenmedi.",
  },
};

export function uploadFailure(error: ApiError): UploadFailure {
  if (error.code && BY_CODE[error.code]) return BY_CODE[error.code]!;

  // The session cookie, which the upload screen cannot recover on its own.
  // Branching on the status the way `AuthGate` does, rather than inventing a
  // new error kind for one screen.
  if (error.status === 401 || error.status === 403) {
    return {
      what: "Oturumunuz doğrulanamadı.",
      next: "Sayfayı yenileyip yeniden giriş yaptıktan sonra bu dosyayı tekrar yükleyin.",
    };
  }

  switch (error.kind) {
    case "canceled":
      return { what: "Yükleme iptal edildi.", next: "Dosyayı yeniden seçerek tekrar yükleyebilirsiniz." };
    case "network":
      return {
        what: "Sunucuya ulaşılamadı; dosya yüklenmedi.",
        next: "Bağlantınızı kontrol edip tekrar deneyin.",
      };
    case "timeout":
      return {
        what: "Yükleme zaman aşımına uğradı.",
        next: "Bağlantınız yavaşsa dosyaları daha küçük gruplar hâlinde yüklemeyi deneyin.",
      };
    case "too_large":
      return BY_CODE.upload_too_large!;
    case "unsupported_media":
      return BY_CODE.upload_unsupported_type!;
    case "not_found":
      return {
        what: "Bu çekim oturumu bulunamadı.",
        next: "Sayfayı yenileyin; oturum silinmiş olabilir.",
      };
    case "unavailable":
    case "server":
      return {
        what: "Sunucuda beklenmeyen bir sorun oluştu; dosya yüklenmedi.",
        next: "Birkaç dakika sonra tekrar deneyin. Diğer dosyalar etkilenmedi.",
      };
    default:
      return {
        what: "Dosya yüklenemedi; nedeni belirlenemedi.",
        next: "Tekrar deneyin; sorun sürerse dosyayı başka bir biçimde kaydetmeyi deneyin.",
      };
  }
}
